<script setup lang="ts" generic="T">
/**
 * A dialog's growing list: search (when it can grow past what one screen shows), a count line
 * ("shown of total · chosen"), and a bounded scroller so the footer never leaves the screen.
 * The rows themselves come from the slot (Choice, table rows, cards) — the list only owns the
 * search, the count and the box. Used wherever a dialog lists project entities that scale.
 */
import { computed, ref, watch } from 'vue'
import SearchBox from './SearchBox.vue'

const props = withDefaults(defineProps<{
  items: T[]
  match?: (item: T, q: string) => boolean
  unit: string
  chosen?: number
  searchFrom?: number
  height?: string
  placeholder?: string
  nothing?: string
  /** Text when the list itself is empty. */
  empty?: string
}>(), { match: undefined, chosen: undefined, searchFrom: 8, height: 'var(--measure-400px)', placeholder: undefined, nothing: 'Nothing matches.', empty: 'Nothing here yet.' })

const query = ref('')
const q = computed(() => query.value.trim().toLowerCase())
const defaultMatch = (item: T, needle: string) => {
  const rec = item as unknown as Record<string, unknown>
  return ['title', 'name', 'facts', 'id'].some((key) => typeof rec[key] === 'string' && rec[key].toLowerCase().includes(needle))
}
const shown = computed(() => !q.value ? props.items : props.items.filter((item) => (props.match ?? defaultMatch)(item, q.value)))
const searchable = computed(() => props.items.length >= props.searchFrom)
const line = computed(() => {
  const parts = [shown.value.length === props.items.length ? `${props.items.length} ${props.unit}` : `${shown.value.length} of ${props.items.length} ${props.unit}`]
  if (props.chosen != null) parts.push(`${props.chosen} chosen`)
  return parts.join(' · ')
})
watch(() => props.items.length, () => { if (!searchable.value) query.value = '' })
defineExpose({ shown })
</script>

<template>
  <div class="flex min-h-0 flex-col gap-1.5">
    <div v-if="searchable || chosen != null" class="flex items-center gap-2">
      <SearchBox v-if="searchable" v-model="query" :placeholder="placeholder ?? 'Search ' + unit + '…'" class="!w-full flex-1" />
      <span class="num ml-auto shrink-0 text-[length:var(--type-fine)] text-muted-foreground">{{ line }}</span>
    </div>
    <div class="flex flex-col gap-1.5 overflow-auto" :style="{ maxHeight: height }">
      <slot :shown="shown" />
      <p v-if="!shown.length" class="m-0 px-1 py-4 text-center text-xs text-muted-foreground">{{ q ? nothing : empty }}</p>
    </div>
  </div>
</template>
